import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconEmail } from '@components/icons/email';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';

import { conditionsTab } from '../conditions-tab';
import { ConfirmationSwitch } from '../confirmation-switch';
import { useFormBuilderContext } from '../context';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { RequiredSwitch } from '../required-switch';

type Config = FieldConfig<'input-email'>;

const Display: Config['Display'] = ({ field }) => {
  return (
    <TextField
      label={asteriskLabel(field.label, field.isRequired)}
      placeholder={field.placeholder}
      labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
    />
  );
};

const FormBuilderEmail: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <TextField
              onChange={actions.createChangeHandler(field, 'placeholder')}
              defaultValue={field.placeholder}
              label={t('placeholder')}
            />
            <RequiredSwitch field={field} />
            {field.isRequired && (
              <ConfirmationSwitch
                onChange={actions.createChangeHandler(field, 'hasConfirmation')}
                value={field.hasConfirmation}
              />
            )}

            {field.hasConfirmation && field.isRequired && (
              <TextField
                onChange={actions.createChangeHandler(field, 'confirmLabel')}
                defaultValue={field.confirmLabel}
                label={t('confirmationLabel')}
              />
            )}
          </>
        }
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Email address',
    sidebarTitle: 'Email address',
    label: 'Label',
    confirmationLabel: 'Confirmation label',
    placeholder: 'Placeholder text',
  },
});

export const emailConfig: Config = {
  kind: 'input-email',
  title: t('title'),
  sidebarTitle: t('sidebarTitle'),
  group: 'inputs',
  propertiesTabs: [conditionsTab],
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Component: FormBuilderEmail,
  Icon: IconEmail,
};
